import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, ShieldCheck, FlaskConical, Sprout } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSiteContent, mergeServices, mergeHome } from "../lib/siteContent";

const heroImg =
  "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB3ZWxsbmVzcyUyMG9mZmljZSUyMHRlYW0lMjB3b3Jrc2hvcHxlbnwwfHx8fDE3Nzg4NDI4MDZ8MA&ixlib=rb-4.1.0&q=85";

const sideImg =
  "https://images.unsplash.com/photo-1543248939-7b514293dc4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85";

const aboutImg =
  "https://images.unsplash.com/photo-1730342754575-0c8ca8e62c0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85";

const DEFAULT_STATS = [
  { v: 250, suffix: "+", k: "programs" },
  { v: 50, suffix: "+", k: "clients" },
  { v: 10, suffix: "K+", k: "participants" },
  { v: 15, suffix: "+", k: "years" },
];

function parseNumeric(value, fallback) {
  if (value == null) return fallback;
  const n = parseInt(String(value).replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : fallback;
}

function useCount(target, on) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!on) return;
    let raf, start;
    const duration = 1400;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setN(Math.floor(target * (0.4 + 0.6 * (1 - Math.pow(1 - p, 3)))));
      if (p < 1) raf = requestAnimationFrame(step);
      else setN(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, on]);
  return n;
}

function StatItem({ v, suffix, label, on }) {
  const n = useCount(v, on);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-1">
        <span className="font-serif text-5xl md:text-6xl text-ink tracking-tightest">{n}</span>
        <span className="font-serif text-3xl md:text-4xl text-terracotta-400">{suffix}</span>
      </div>
      <span className="text-xs uppercase tracking-[0.22em] text-ink/55">{label}</span>
    </div>
  );
}

function StatsRow({ home }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setOn(true), { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const stats = DEFAULT_STATS.map((s) => {
    const dyn = home.statsValues?.[s.k];
    return {
      ...s,
      v: dyn ? parseNumeric(dyn.value, s.v) : s.v,
      suffix: dyn?.suffix ?? s.suffix,
    };
  });

  return (
    <section ref={ref} data-testid="stats-section" className="border-y border-cream-300/70 bg-cream-200/40">
      <div className="container-x py-14 md:py-20 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
        {stats.map((s) => (
          <StatItem key={s.k} v={s.v} suffix={s.suffix} label={home.stats[s.k]} on={on} />
        ))}
      </div>
    </section>
  );
}

const pillarIcons = [ShieldCheck, FlaskConical, Sparkles, Sprout];

export default function Home() {
  const { t, lang } = useLang();
  const { content } = useSiteContent();
  const services = mergeServices(t.services.items, lang, content?.services);
  const home = mergeHome(t.home, lang, content?.home);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-20 -right-20 w-[40rem] h-[40rem] rounded-full bg-sage-100/60 blur-3xl" />
          <div className="absolute top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-terracotta-50/80 blur-3xl" />
        </div>
        <div className="container-x pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="label-eyebrow">{home.eyebrow}</span>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[5.25rem] leading-[1.02] tracking-tight text-ink">
              {home.heroTitle.split(" ").slice(0, -2).join(" ")}{" "}
              <span className="italic text-terracotta-400">
                {home.heroTitle.split(" ").slice(-2).join(" ")}
              </span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-2xl">
              {home.heroLead}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/services" data-testid="hero-cta-primary" className="btn-primary">
                {home.ctaPrimary}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-secondary" className="btn-secondary">
                {home.ctaSecondary}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full rounded-3xl overflow-hidden shadow-float">
              <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-sage-800/30 via-transparent" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-8 w-40 h-40 rounded-2xl overflow-hidden shadow-float border-4 border-cream-100">
              <img src={sideImg} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">{home.philosophyEyebrow}</span>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink">
              {home.philosophyTitle}
            </h2>
            <p className="mt-6 text-lg text-ink/70 font-light leading-relaxed">
              {home.philosophyBody}
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {home.pillars.map((p, i) => {
              const Icon = pillarIcons[i % pillarIcons.length];
              const tones = [
                "bg-sage-100 text-sage-700",
                "bg-terracotta-50 text-terracotta-500",
                "bg-cream-200 text-ink",
                "bg-bluefog-200 text-sage-800",
              ];
              return (
                <div
                  key={p.t}
                  data-testid={`pillar-${i}`}
                  className={`group rounded-2xl p-7 border border-cream-300/70 ${tones[i]} transition-all duration-500 hover:-translate-y-1 hover:shadow-float`}
                >
                  <Icon className="w-7 h-7 mb-5" strokeWidth={1.4} />
                  <h3 className="font-serif text-2xl text-ink leading-snug">{p.t}</h3>
                  <p className="mt-2 text-sm font-light text-ink/65 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-cream-200/40 border-y border-cream-300/70">
        <div className="container-x py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="label-eyebrow">{t.services.eyebrow}</span>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink max-w-3xl">
                {t.services.title}
              </h2>
            </div>
            <Link to="/services" data-testid="services-see-all" className="text-sm text-ink/70 hover:text-terracotta-400 inline-flex items-center gap-1 group">
              {t.common.ourServices}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {services.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services#${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group surface-card p-8 md:p-10 hover:-translate-y-1 transition-all duration-500 flex flex-col"
              >
                <span className="text-xs uppercase tracking-[0.22em] text-terracotta-400 font-medium">{s.eyebrow}</span>
                <h3 className="mt-5 font-serif text-3xl md:text-4xl text-ink leading-tight">{s.t}</h3>
                <p className="mt-4 text-base text-ink/65 font-light leading-relaxed">{s.d}</p>
                <span className="mt-8 inline-flex items-center gap-1.5 text-sm text-ink/80 group-hover:text-terracotta-400 transition-colors">
                  {t.common.learnMore}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="container-x py-24 md:py-32">
        <div className="max-w-3xl mb-14">
          <span className="label-eyebrow">{home.whyEyebrow}</span>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink">
            {home.whyTitle}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {home.whyItems.map((w, i) => (
            <div
              key={w.t}
              data-testid={`why-card-${i}`}
              className="surface-card p-7 hover:-translate-y-1 hover:shadow-float transition-all duration-500"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-3xl text-terracotta-400">0{i + 1}</span>
                <h3 className="font-serif text-2xl text-ink leading-tight">{w.t}</h3>
              </div>
              <p className="mt-3 text-sm font-light text-ink/65 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-x pb-24 md:pb-32">
        <div className="relative overflow-hidden rounded-3xl bg-sage-800 text-cream-100 p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-terracotta-400/20 blur-3xl" />
          <div className="lg:col-span-7 relative z-10">
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
              {home.finalCtaTitle}
            </h2>
            <p className="mt-5 text-cream-100/75 max-w-xl font-light text-base md:text-lg">
              {home.finalCtaBody}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="final-cta-contact"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-cream-100 px-7 py-3.5 text-sm font-light transition-all hover:-translate-y-0.5"
              >
                {t.common.getInTouch}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/services"
                data-testid="final-cta-services"
                className="inline-flex items-center gap-2 rounded-full border border-cream-100/30 hover:bg-cream-100 hover:text-ink text-cream-100 px-7 py-3.5 text-sm font-light transition-all hover:-translate-y-0.5"
              >
                {t.common.ourServices}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative z-10">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src={aboutImg} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
